import { useEffect, useState } from "react";
import ReactSelect from "react-select";
import EstadoBadge from "../components/EstadoBadge";
import {
  listAcceso,
  changeUsuariosAcceso,
  createUsuariosAcceso,
  editUsuariosAcceso,
} from "../api/usuarioSucursales";
import { listPermisos } from "../api/permisos";
import { listUsuarios } from "../api/usuarios";
import Loader from "./Loader";

const Accesos = () => {
  const [loading, setLoading] = useState(true);
  const [accesos, setAccesos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [permisos, setPermisos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const fetchData = async () => {
    try {
      const data = await listAcceso({});
      setAccesos(data.data);

      const per = await listPermisos({});
      setPermisos(per.data);

      const user = await listUsuarios({});
      setUsuarios(user.data);

    } catch (error) {
      console.error("Error al obtener los accesos", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const [form, setForm] = useState({
    id_usuario: "",
    id_permiso: "",
    status: "",
  });

  const openModalForNew = () => {
    setForm({ id_usuario: "", id_permiso: "", status: "" });
    setEditMode(false);
    setCurrentId(null);
    setShowModal(true);
  };

  const openModalForEdit = (acceso) => {
    setForm({
      id_usuario: acceso?.Usuario?.id?.toString() || "",
      id_permiso: acceso?.Permiso?.id?.toString() || "",
      status: acceso.status?.toString() || "",
    });
    setCurrentId(acceso.id);
    setEditMode(true);
    setShowModal(true);
  };

  const handleToggleStatus = async (acceso) => {
    const newStatus = acceso.status === 1 ? 0 : 1;
    try {
      await changeUsuariosAcceso(acceso.id, { status: newStatus });
      await fetchData();
    } catch (error) {
      console.error("Error al cambiar el estado", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = {
        id_usuario: form.id_usuario,
        id_permiso: form.id_permiso,
        status: form.status,
      };

      if (editMode) {
        await editUsuariosAcceso(currentId, data);
      } else {
        await createUsuariosAcceso(data);
      }

      await fetchData();
      setShowModal(false);
    } catch (error) {
      console.error("Error al guardar el acceso", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value, name) => {
    setForm((prev) => ({ ...prev, [name]: value ? value.value : "" }));
  };

  const opcionesPermiso = permisos.map((p) => ({
    value: p.id.toString(),
    label: `${p.permiso} | ${p.Sucursale?.codigo_sucursal || "Sin sucursal"} | ${p.Sucursale?.nombre || "Sin nombre"}`,
  }));

  const opcionesUsuario = usuarios.map((u) => ({
    value: u.id.toString(),
    label: `${u.codigo} | ${u.username} | ${u.nombre + " " + u.apellido}`,
  }));

  if (loading) return <Loader />;

  // Agrupar por usuario
  const accesosAgrupados = accesos.reduce((acc, acceso) => {
    //   const codigo = acceso.Usuario?.codigo || "SIN_CODIGO";
    const codigo = acceso.Usuario?.nombre + " " + acceso.Usuario?.apellido || "------";
    if (!acc[codigo]) acc[codigo] = [];
    acc[codigo].push(acceso);
    return acc;
  }, {});

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Gestión de Accesos</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Administre los permisos y sucursales de los usuarios</p>
        </div>
        <button
          onClick={openModalForNew}
          className="w-full sm:w-auto bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-200 font-medium flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Agregar Acceso
        </button>
      </div>

      {/* Grupos por usuario */}
      <div className="space-y-4 sm:space-y-6">
        {Object.keys(accesosAgrupados).map((codigoUsuario) => (
          <div key={codigoUsuario} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Cabecera del grupo */}
            <div className="bg-slate-50 border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold shrink-0 text-sm">
                {codigoUsuario.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block">Usuario</span>
                <span className="text-slate-800 font-bold text-sm sm:text-base">{codigoUsuario}</span>
              </div>
            </div>

            {/* Tabla — solo en md+ */}
            <div className="hidden md:block overflow-x-auto custom-scrollbar">
              <table className="w-full text-sm text-left whitespace-nowrap">
                <thead className="text-xs text-slate-500 bg-white uppercase border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-3 font-semibold">Código</th>
                    <th className="px-6 py-3 font-semibold">UserName</th>
                    <th className="px-6 py-3 font-semibold">Sucursal</th>
                    <th className="px-6 py-3 font-semibold">Acceso</th>
                    <th className="px-6 py-3 font-semibold text-center w-32">Estado</th>
                    <th className="px-6 py-3 font-semibold text-center w-40">Opción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {accesosAgrupados[codigoUsuario].map((acceso) => (
                    <tr
                      key={acceso.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4 text-slate-500 font-medium">{acceso.Usuario?.codigo}</td>
                      <td className="px-6 py-4 text-slate-700 font-medium">{acceso.Usuario?.username}</td>
                      <td className="px-6 py-4 text-slate-600">
                        {acceso.Permiso?.Sucursale?.codigo_sucursal
                          ? `${acceso.Permiso?.Sucursale?.codigo_sucursal} | ${acceso.Permiso?.Sucursale?.nombre}`
                          : <span className="text-slate-400 italic">Sin sucursal asignada</span>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-slate-800 font-semibold">{acceso.Permiso?.permiso || "Sin permiso"}</span>
                          <span className="text-xs text-slate-500 truncate max-w-[200px]" title={acceso.Permiso?.description}>{acceso.Permiso?.description || ""}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <EstadoBadge status={acceso.status} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => openModalForEdit(acceso)}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors border border-transparent hover:border-indigo-100"
                            title="Editar acceso"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {acceso.status === 1 ? (
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(acceso)}
                              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors border border-transparent hover:border-rose-100"
                              title="Deshabilitar acceso"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              </svg>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(acceso)}
                              className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors border border-transparent hover:border-emerald-100"
                              title="Activar acceso"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Sub-cards — solo en móvil */}
            <div className="md:hidden divide-y divide-slate-100">
              {accesosAgrupados[codigoUsuario].map((acceso) => (
                <div key={acceso.id} className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-sm">
                        {acceso.Permiso?.permiso || "Sin permiso"}
                      </p>
                      {acceso.Permiso?.description && (
                        <p className="text-xs text-slate-500 truncate">{acceso.Permiso.description}</p>
                      )}
                    </div>
                    <EstadoBadge status={acceso.status} />
                  </div>

                  <div className="text-xs text-slate-600 mb-3">
                    {acceso.Permiso?.Sucursale?.codigo_sucursal ? (
                      <span className="inline-flex items-center gap-1">
                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        {acceso.Permiso.Sucursale.codigo_sucursal} | {acceso.Permiso.Sucursale.nombre}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic">Sin sucursal asignada</span>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => openModalForEdit(acceso)}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-white border border-slate-200 text-slate-600 py-2 rounded-lg hover:bg-slate-50 hover:text-indigo-600 transition-colors text-xs font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Editar
                    </button>
                    {acceso.status === 1 ? (
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(acceso)}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-red-50 text-red-600 border border-red-100 py-2 rounded-lg hover:bg-red-100 transition-colors text-xs font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                        Deshabilitar
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(acceso)}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-50 text-emerald-600 border border-emerald-100 py-2 rounded-lg hover:bg-emerald-100 transition-colors text-xs font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Activar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-start sm:items-center z-[100] p-2 sm:p-4 overflow-y-auto animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-fade-in-up my-4 sm:my-0">
            <div className="bg-slate-50 border-b border-slate-100 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <h3 className="text-base sm:text-xl font-bold text-slate-800 flex items-center gap-2">
                {editMode ? (
                  <>
                    <svg className="w-5 h-5 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Editar Acceso
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Agregar Nuevo Acceso
                  </>
                )}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-full hover:bg-slate-200"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-5">
              <div>
                <label className="block mb-2 text-sm font-semibold text-slate-700">Usuario</label>
                <ReactSelect
                  options={opcionesUsuario}
                  value={opcionesUsuario.find((opt) => opt.value === form.id_usuario) || null}
                  onChange={(val) => handleSelectChange(val, "id_usuario")}
                  placeholder="Seleccione un usuario"
                  isSearchable
                  isClearable
                  classNamePrefix="react-select"
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#6366f1' : '#cbd5e1',
                      borderRadius: '0.5rem',
                      padding: '2px',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.2)' : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? '#6366f1' : '#94a3b8'
                      }
                    })
                  }}
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-semibold text-slate-700">Permiso</label>
                <ReactSelect
                  options={opcionesPermiso}
                  value={opcionesPermiso.find((opt) => opt.value === form.id_permiso) || null}
                  onChange={(val) => handleSelectChange(val, "id_permiso")}
                  placeholder="Seleccione el acceso"
                  isSearchable
                  isClearable
                  classNamePrefix="react-select"
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#6366f1' : '#cbd5e1',
                      borderRadius: '0.5rem',
                      padding: '2px',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.2)' : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? '#6366f1' : '#94a3b8'
                      }
                    })
                  }}
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-semibold text-slate-700">Estado</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-4 py-2.5 font-medium transition-colors focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm
                    ${form.status === "1" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                      form.status === "0" ? "bg-rose-50 text-rose-800 border-rose-200" :
                        "bg-white border-slate-300 text-slate-700"}`}
                  required
                >
                  <option value="" disabled className="text-slate-400">Selecciona el estado</option>
                  <option value="1" className="text-emerald-700 bg-white">Activo</option>
                  <option value="0" className="text-rose-700 bg-white">Inactivo</option>
                </select>
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 mt-6 pt-5 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="w-full sm:w-auto px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 focus:ring-2 focus:ring-slate-200 transition-colors text-sm"
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full sm:w-auto px-6 py-2.5 rounded-lg flex items-center justify-center gap-2 font-medium shadow-sm transition-all focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-white text-sm
                    ${submitting ? "bg-indigo-400 cursor-not-allowed shadow-none" : "bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-200"}`}
                >
                  {submitting && (
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  )}
                  {submitting
                    ? editMode
                      ? "Actualizando..."
                      : "Guardando..."
                    : editMode
                      ? "Actualizar Acceso"
                      : "Guardar Acceso"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accesos;
